import type { Dispatch, SetStateAction } from 'react';
import { getApiUrl } from '../../utils/getApiUrl';
import type { WordModel } from '../../type/word';
import { fetchWithAuth } from '../../auth/auth';


export const addWord = async (
  eng: string,
  pl: string,
  iddictionary: string | null,
  showSpinner: () => void,
  hideSpinner: () => void,
  showToast: (msg: string, type?: 'info' | 'warning' | 'error') => void,
  onSuccess: (newWord: WordModel) => void  // Changed: receives the new expression
) => {
  if (!eng || !pl) {
    showToast("Enter both English and Polish words.", "warning");
    return;
  }

  const dictId = iddictionary ? parseInt(iddictionary, 10) : undefined;

  const newWord: WordModel = {
    eng,
    pl,
    iddictionary: Number.isNaN(dictId) ? undefined : dictId,
    eng_pronunciation: "",
    deleted: false
  };

  try {
    showSpinner();
    const resp = await fetchWithAuth(getApiUrl("words/add"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newWord),
    });

    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

    const createdWord = await resp.json();  // Now receives full WordModel with ID
    onSuccess(createdWord);
  } catch (err) {
    console.error(err);
    showToast("Failed to add expression.", "error");
  } finally {
    hideSpinner();
  }
};

export const deleteWord = async (
  id: number,
  setAllWords: Dispatch<SetStateAction<WordModel[]>>,
  setFilteredWords: Dispatch<SetStateAction<WordModel[]>>,
  showSpinner: () => void,
  hideSpinner: () => void,
  showToast: (msg: string, type?: 'info' | 'warning' | 'error') => void,
) => {
  //if (!window.confirm("Delete this word?")) return;
  
  try {
    showSpinner();
    const resp = await fetchWithAuth(getApiUrl(`words/delete/${id}`), { method: "DELETE" });
    if (!resp.ok) {
      showToast("Failed to delete word.", "error");
      return;
    }

    setAllWords(prev => prev.filter(w => w.idexpression !== id));
    setFilteredWords(prev => prev.filter(w => w.idexpression !== id));
    document.dispatchEvent(new CustomEvent("wordcounts:refresh"));
  } catch (err) {
    console.error(err);
    showToast("Error deleting word.", "error");
  }
  finally {
    hideSpinner();
  }
};

export const updateWord = async (
  id: number,
  eng: string,
  pl: string,
  allWords: WordModel[],
  filteredWords: WordModel[],
  setAllWords: (words: WordModel[]) => void,
  setFilteredWords: (words: WordModel[]) => void,
  sortWords: (words: WordModel[], column: keyof WordModel, order: "asc" | "desc") => WordModel[],
  showSpinner: () => void,
  hideSpinner: () => void,
  showToast: (msg: string, type?: 'info' | 'warning' | 'error') => void,
  currentSort: { column: keyof WordModel; order: "asc" | "desc" }
) => {
  if (!eng || !pl) {
    showToast("Enter both English and Polish words.", "warning");
    return;
  }
  
  try {
    showSpinner();
    const resp = await fetchWithAuth(getApiUrl(`words/update/${id}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eng, pl }),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

    const updatedAll = allWords.map(w => (w.idexpression === id ? { ...w, eng, pl } : w));
    setAllWords(updatedAll);

    const updatedFiltered = filteredWords.map(w => (w.idexpression === id ? { ...w, eng, pl } : w));
    const sorted = sortWords(updatedFiltered, currentSort.column, currentSort.order);
    setFilteredWords(sorted);
  } catch (err) {
    console.error(err);
    showToast("Failed to update word.", "error");
  }
  finally {
    hideSpinner();
  }
};

export const moveWord = async (
  wordId: number,
  targetDictId: number,
  showSpinner: () => void,
  hideSpinner: () => void,
  showToast: (msg: string, type?: 'info' | 'warning' | 'error') => void,
  onSuccess: () => void
) => {
  try {
    showSpinner();
    const resp = await fetchWithAuth(getApiUrl(`words/move/${wordId}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ iddictionary: targetDictId }),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    document.dispatchEvent(new CustomEvent("wordcounts:refresh"));
    onSuccess();
  } catch (err) {
    console.error(err);
    showToast("Failed to move word.", "error");
  }
  finally {
    hideSpinner();
  }
};